import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Import hooks
import { supabase } from '../../lib/supabase'; // Adjust path
import { useAuth } from '../../contexts/AuthContext'; // Adjust path
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepButton,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

// Reuse or redefine Request interface if needed
interface AdminRequest {
  id: string;
  organization_id: string;
  requesting_user_id: string;
  approving_user_id: string | null;
  request_type: string;
  status: string;
  request_details: any; // JSONB
  superadmin_notes: string | null;
  created_at: string;
  updated_at: string;
  // Potentially join org name, user email etc. if needed directly here
}

const steps = ['Assign SIMs', 'Adjust Wallet', 'Complete'];

export function ProcessRequest() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth(); // For user ID when updating

  const [request, setRequest] = useState<AdminRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0); // For stepper UI
  
  // Add state for SIM assignment step
  const [simsToAssign, setSimsToAssign] = useState<Array<{ msisdn: string; iccid: string }>>([]);
  const [simAssignmentComplete, setSimAssignmentComplete] = useState(false);
  const [isAssigningSims, setIsAssigningSims] = useState(false);

  // Add state for Wallet adjustment step
  const [isAdjustingWallet, setIsAdjustingWallet] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false); // State for confirmation dialog

  // Fetch the specific request
  useEffect(() => {
    const fetchRequestDetails = async () => {
      if (!requestId) {
        setError('Request ID not found in URL.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Fetch request, maybe join org name? 
        const { data, error: fetchError } = await supabase
          .from('admin_requests')
          .select('*, organizations ( name ) ') // Example join
          .eq('id', requestId)
          .maybeSingle(); // Use maybeSingle as ID should be unique

        if (fetchError) throw fetchError;
        if (!data) {
          throw new Error('Request not found or access denied.');
        }
        if (data.status !== 'processing') {
            // Optional: Redirect if status is not processing, or show message
            console.warn('Request status is not processing:', data.status);
            setError(`This request is already in status: ${data.status}`);
             // navigate('/admin/manage-requests');
             // return;
        }

        // Set the main request state *first*
        setRequest(data as any); 

        // Initialize simsToAssign ONLY if the request state was previously null (i.e., initial load)
        if (!request && data.request_type === 'bulk_sim_upload' && data.request_details?.numSims > 0) {
             const numSims = data.request_details.numSims;
             console.log('Initializing simsToAssign for the first time');
             setSimsToAssign(Array(numSims).fill({ msisdn: '', iccid: '' }));
        } else if (!request && !(data.request_type === 'bulk_sim_upload' && data.request_details?.numSims > 0)) {
            // Handle initial load where SIM assignment isn't needed
             console.log('SIM assignment step not applicable on initial load');
             setSimAssignmentComplete(true); 
             setActiveStep(1); 
        }

      } catch (err: any) {
        console.error("Error fetching request details:", err);
        setError(err.message || 'Failed to load request details.');
        setRequest(null);
      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [requestId, navigate, request]);

  // --- Placeholder Handlers --- 
  const handleSimInputChange = (index: number, field: 'msisdn' | 'iccid', value: string) => {
    const updatedSims = [...simsToAssign];
    updatedSims[index] = { ...updatedSims[index], [field]: value };
    setSimsToAssign(updatedSims);
  };

  const handleConfirmSimAssignment = useCallback(async () => {
     console.log("Validating SIM Assignment Input...", simsToAssign);
     setIsAssigningSims(true); // Start loading indicator
     setError(null);

     // --- Basic Validation --- 
     if (!request || !request.request_details?.clusterId) {
         setError("Request details or cluster ID is missing.");
         setIsAssigningSims(false);
         return;
     }
     const trimmedSims = simsToAssign.map(sim => ({ msisdn: sim.msisdn?.trim() ?? '', iccid: sim.iccid?.trim() ?? '' }));
     const invalidSim = trimmedSims.find(sim => !sim.msisdn || !sim.iccid);
     if (invalidSim) {
        setError("Please fill in all MSISDN and ICCID fields.");
        setIsAssigningSims(false);
        return;
     }
     // TODO: Add more robust validation if needed (e.g., length, format)

     // --- Duplicate Check --- 
     try {
        const msisdnsToCheck = trimmedSims.map(sim => sim.msisdn);
        const iccidsToCheck = trimmedSims.map(sim => sim.iccid);

        console.log("Checking for duplicate MSISDNs:", msisdnsToCheck);
        console.log("Checking for duplicate ICCIDs:", iccidsToCheck);

        const { data: duplicateSims, error: duplicateCheckError } = await supabase
            .from('sim_cards')
            .select('msisdn, iccid')
            .or(`msisdn.in.(${msisdnsToCheck.join(',')}),iccid.in.(${iccidsToCheck.join(',')})`);

        if (duplicateCheckError) {
            console.error("Error checking for duplicate SIMs:", duplicateCheckError);
            throw new Error(`Failed to check for duplicates: ${duplicateCheckError.message}`);
        }

        if (duplicateSims && duplicateSims.length > 0) {
            console.warn("Duplicate SIMs found:", duplicateSims);
            const duplicateMessages = duplicateSims.map(dup => 
                `${dup.msisdn ? `MSISDN ${dup.msisdn}` : ''}${dup.msisdn && dup.iccid ? '/' : ''}${dup.iccid ? `ICCID ${dup.iccid}` : ''}`
            );
            setError(`Duplicate(s) found in database: ${duplicateMessages.join('; ')}. Please correct the entries.`);
            setIsAssigningSims(false);
            return; // Stop processing
        }

        console.log("No duplicates found.");

        // --- Validation Passed --- 
        console.log("SIM input validated (including duplicates). Proceeding...");
        setSimAssignmentComplete(true); // Mark step as complete
        setActiveStep(1); // Move to next step
        
     } catch (err: any) { 
        console.error("Error during SIM assignment confirmation:", err);
        setError(err.message || "An error occurred during SIM validation.");
     } finally {
        setIsAssigningSims(false); // Stop loading indicator regardless of outcome
     }

  }, [simsToAssign, request]); // Keep dependencies

   const handleConfirmDeduction = useCallback(async () => {
    setConfirmDialogOpen(false); 
    console.log("Attempting to complete request processing...");
    setIsAdjustingWallet(true); // Reuse this state for loading indicator
    setError(null);

    if (!request || !profile?.id) {
        setError("Request data or user profile is missing.");
        setIsAdjustingWallet(false);
        return;
    }
    
    // Prepare SIM data only if the assignment step was relevant and completed
    let simDataForRpc = null;
    if (request.request_type === 'bulk_sim_upload' && simAssignmentComplete && simsToAssign.length > 0) {
        // Validate sims again briefly before sending?
        const invalidSim = simsToAssign.find(sim => !sim.msisdn?.trim() || !sim.iccid?.trim());
         if (invalidSim) {
            setError("Cannot complete: Invalid SIM data detected. Please go back and correct MSISDN/ICCID.");
            setIsAdjustingWallet(false);
            return;
         }
        simDataForRpc = simsToAssign.map(sim => ({ msisdn: sim.msisdn.trim(), iccid: sim.iccid.trim() }));
    }

    try {
        console.log(`Calling process_sim_request_completion for request ${request.id}`);
        const { data: rpcResult, error: rpcError } = await supabase.rpc(
            'process_sim_request_completion', 
            {
                p_request_id: request.id,
                p_sim_data: simDataForRpc, // Pass null if SIM assignment wasn't needed/done
                p_admin_user_id: profile.id
            }
        );

        if (rpcError) {
            // Handle unexpected errors calling the RPC function itself
            console.error("RPC Call Error:", rpcError);
            throw new Error(`Failed to call completion function: ${rpcError.message}`);
        }

        console.log("RPC Result:", rpcResult);

        // Check the business logic success/error returned by the function
        if (rpcResult && rpcResult.success) {
            console.log("Request completion successful via RPC.");
            // Update local state to reflect completion for the final step display
            // Note: We might need to refetch request data for the most accurate final view,
            // but for now, just advance the step.
            // setWalletAdjustmentComplete(true); // No longer strictly needed? Set activeStep instead.
            setActiveStep(2); // Move to the final step
        } else {
            // Business logic error occurred (e.g., insufficient funds, duplicate SIM detected by RPC)
            console.error("RPC returned failure:", rpcResult?.error);
            throw new Error(rpcResult?.error || "Request processing failed. Check logs.");
        }

    } catch (err: any) { 
         console.error("Error during request completion:", err);
        setError(err.message || "Failed during final request processing step.");
        // Do not advance step on error
    } finally {
       setIsAdjustingWallet(false); // Stop loading indicator
    }
   // Dependencies might change slightly - simAssignmentComplete matters now
   }, [request, profile, simsToAssign, simAssignmentComplete]);

   // Handlers for dialog
   const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
   };
   const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
   };
   const handleDialogConfirm = () => {
     handleConfirmDeduction(); // Call the actual deduction logic
   };

  // Handler for clicking on a stepper step
  const handleStepClick = (stepIndex: number) => {
    // Allow navigating back only
    if (stepIndex < activeStep) {
        // Optionally reset error when navigating back?
        // setError(null);
        setActiveStep(stepIndex);
    }
  };

  // --- Render Logic --- 
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!request) {
     return <Container maxWidth="md"><Alert severity="warning" sx={{ mt: 4 }}>Request data could not be loaded.</Alert></Container>;
  }

  // Calculate cost here for wallet step
  const calculatedCost = request.request_details?.totalSetupCost ?? 0;
  const details = request.request_details;
  const orgName = (request as any).organizations?.name || request.organization_id;

  return (
    <Container maxWidth="lg"> {/* Wider container */} 
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Process Request: {request.id}
        </Typography>
         <Typography variant="subtitle1" gutterBottom>
            Request Type: {request.request_type.replace(/_/g, ' ')} | Status: {request.status.toUpperCase()}
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => {
            const stepProps: { completed?: boolean } = {};
            // Mark steps as completed based on state
            if (index === 0 && simAssignmentComplete) {
                stepProps.completed = true;
            }
            if (index === 1 && simAssignmentComplete) {
                 stepProps.completed = true;
            }
            return (
              <Step key={label} {...stepProps}>
                 {/* Use StepButton for clickability */}
                <StepButton 
                    onClick={() => handleStepClick(index)}
                    disabled={index >= activeStep} // Disable future/current steps
                >
                  {label}
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        {/* --- Display Error Inline --- */}
        {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
            </Alert>
        )}
        {/* --- End Display Error Inline --- */}

        {/* Step Content */} 
        <Paper sx={{ p: 3 }}>
            {/* Show content based only on activeStep */}
            {activeStep === 0 && (
                <Box>
                    <Typography variant="h6">Step 1: Assign SIMs</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                         Enter MSISDN and ICCID for the requested {request.request_details?.numSims || 'N/A'} SIM(s).
                    </Typography>
                    
                    {/* --- SIM Input Table --- */}
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: '10%' }}>#</TableCell>
                                    <TableCell>MSISDN</TableCell>
                                    <TableCell>ICCID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {simsToAssign.map((sim, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                fullWidth
                                                value={sim.msisdn}
                                                onChange={(e) => handleSimInputChange(index, 'msisdn', e.target.value)}
                                                // TODO: Add validation (e.g., required, numeric)
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                variant="standard"
                                                fullWidth
                                                value={sim.iccid}
                                                onChange={(e) => handleSimInputChange(index, 'iccid', e.target.value)}
                                                 // TODO: Add validation (e.g., required, numeric, length)
                                           />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* --- End SIM Input Table --- */}

                    {/* TODO: Add CSV Upload Option here */} 
                     <Typography color="text.secondary" sx={{mt:2}}> CSV Upload Placeholder</Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}> 
                        <Button 
                            variant="contained" 
                            onClick={() => simAssignmentComplete ? setActiveStep(1) : handleConfirmSimAssignment()} 
                            disabled={isAssigningSims} 
                        >
                            {isAssigningSims 
                                ? <CircularProgress size={24} /> 
                                : simAssignmentComplete 
                                    ? 'Next' 
                                    : 'Confirm SIM Assignment'
                            }
                        </Button>
                    </Box>
                </Box>
            )}

            {/* Show content based only on activeStep */}
            {activeStep === 1 && (
                 <Box>
                    <Typography variant="h6">Step 2: Adjust Wallet</Typography>
                     <Typography variant="body1" sx={{ mb: 2 }}>
                         Please review the request details before confirming the wallet deduction.
                     </Typography>
                    
                    {/* --- Request Summary --- */}
                    <Typography variant="subtitle1" gutterBottom>Request Summary:</Typography>
                    <List dense disablePadding sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                         <ListItem disableGutters>
                            <ListItemText primary="Organization" secondary={orgName} />
                         </ListItem>
                         <ListItem disableGutters>
                            <ListItemText primary="Request ID" secondary={request.id} />
                         </ListItem>
                         {details?.numSims && <ListItem disableGutters>
                            <ListItemText primary="Number of SIMs" secondary={details.numSims} />
                         </ListItem>}
                         {details?.clusterName && <ListItem disableGutters>
                            <ListItemText primary="Target Cluster" secondary={details.clusterName} />
                         </ListItem>}
                          {details?.productName && <ListItem disableGutters>
                            <ListItemText primary="Initial Product" secondary={`${details.productName} (${formatCurrency(details.productCost)})`} />
                         </ListItem>}
                          <ListItem disableGutters>
                            <ListItemText primary="Total Setup Cost (Deduction Amount)" secondary={formatCurrency(calculatedCost)} sx={{ fontWeight: 'bold' }}/>
                         </ListItem>
                    </List>
                    {/* --- End Request Summary --- */}                    

                     <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}> 
                        <Button 
                            variant="outlined"
                            onClick={() => handleStepClick(0)}
                            disabled={isAdjustingWallet}
                        >
                            Back
                        </Button>
                        <Button 
                            variant="contained" 
                            onClick={handleOpenConfirmDialog} 
                            disabled={isAdjustingWallet || !simAssignmentComplete}
                        >
                            {isAdjustingWallet ? <CircularProgress size={24} /> : 'Confirm Deduction'}
                        </Button>
                    </Box>

                     {/* --- Confirmation Dialog --- */}
                     <Dialog
                        open={confirmDialogOpen}
                        onClose={handleCloseConfirmDialog}
                      >
                        <DialogTitle>Confirm Wallet Deduction</DialogTitle>
                        <DialogContent>
                          <DialogContentText>
                            Are you sure you want to deduct {formatCurrency(calculatedCost)} from the wallet of organization "{orgName}" for request {request.id}?
                            This action cannot be undone.
                          </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={handleCloseConfirmDialog} disabled={isAdjustingWallet}>Cancel</Button>
                          <Button onClick={handleDialogConfirm} variant="contained" color="primary" disabled={isAdjustingWallet} autoFocus>
                            {isAdjustingWallet ? <CircularProgress size={24} /> : 'Confirm'}
                          </Button>
                        </DialogActions>
                      </Dialog>
                      {/* --- End Confirmation Dialog --- */} 
                </Box>
            )}

            {activeStep === 2 && (
                 <Box>
                    <Typography variant="h6">Step 3: Complete</Typography>
                    <Alert severity="success" sx={{mt: 2, mb: 2}}>Request processed successfully!</Alert>

                    {/* --- Completion Summary --- */}
                    <Typography variant="subtitle1" gutterBottom>Completion Summary:</Typography>
                    <List dense disablePadding sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                         <ListItem disableGutters>
                            <ListItemText primary="Request ID" secondary={request.id} />
                         </ListItem>
                         <ListItem disableGutters>
                            <ListItemText primary="Organization" secondary={orgName} />
                         </ListItem>
                         {simAssignmentComplete && <ListItem disableGutters>
                            <ListItemText primary="SIMs Assigned" secondary={`${simsToAssign.length} SIM(s) assigned to cluster ${details?.clusterName || 'N/A'}.`} />
                         </ListItem>}
                         <ListItem disableGutters>
                            <ListItemText primary="Final Status" secondary={request.status.toUpperCase()} />
                         </ListItem>
                    </List>
                    {/* --- End Completion Summary --- */}  

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}> 
                        <Button 
                            variant="outlined"
                            onClick={() => handleStepClick(1)}
                        >
                            Back
                        </Button>
                         <Button variant="contained" onClick={() => navigate('/admin/requests')} >
                            Finish & View All Requests
                         </Button>
                     </Box>
                 </Box>
            )}
        </Paper>
      </Box>
    </Container>
  );

  // Helper function (ensure it exists or add it)
  function formatCurrency(amount: number | null | undefined): string {
    if (amount === null || amount === undefined) return 'R-.--';
    return `R${amount.toFixed(2)}`;
  }
} 